/**
 * Cookie Notice Banner Handler
 *
 * Manages the display and dismissal of the cookie consent banner.
 * Stores user consent in localStorage with a 30-day expiration.
 *
 * Storage Format:
 * {
 *   "dismissed": true,
 *   "timestamp": 1703001234567,
 *   "expiresAt": 1705593234567
 * }
 *
 * ACCESSIBILITY:
 * - Respects prefers-reduced-motion for slide animation
 * - ARIA labels for screen readers
 * - Keyboard accessible (Tab, Enter, Space)
 *
 * FEATURES:
 * - Slide-down animation on dismissal
 * - Slide-up animation on appearance
 * - 30-day expiration with automatic re-display
 * - Theme editor compatible
 * - Graceful degradation if localStorage unavailable
 */

(function () {
  'use strict';

  // Configuration
  const STORAGE_KEY = 'axiom_cookie_consent';
  const EXPIRATION_DAYS = 30;
  const SLIDE_DURATION = 300; // milliseconds

  /**
   * Check if user prefers reduced motion
   * @returns {boolean}
   */
  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Check if consent has been given and is still valid
   * @returns {boolean}
   */
  function hasValidConsent() {
    try {
      const data = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

      if (!data.dismissed) {
        return false;
      }

      // Check if consent has expired
      if (data.expiresAt && Date.now() > data.expiresAt) {
        // Expired - clear storage
        localStorage.removeItem(STORAGE_KEY);
        return false;
      }

      return true;
    } catch (error) {
      console.warn('Cookie notice: Failed to read consent status:', error);
      return false;
    }
  }

  /**
   * Store consent with expiration
   */
  function storeConsent() {
    try {
      const now = Date.now();
      const expiresAt = now + EXPIRATION_DAYS * 24 * 60 * 60 * 1000;

      const data = {
        dismissed: true,
        timestamp: now,
        expiresAt: expiresAt,
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn('Cookie notice: Failed to store consent:', error);
    }
  }

  /**
   * Dismiss the cookie notice with animation
   */
  function dismissNotice() {
    const banner = document.getElementById('cookie-notice-banner');
    if (!banner) return;

    // Store consent
    storeConsent();

    // Apply slide-down animation
    if (prefersReducedMotion()) {
      // No animation - instant hide
      banner.classList.add('hidden');
    } else {
      // Slide down animation
      banner.style.transition = `transform ${SLIDE_DURATION}ms ease-in-out, opacity ${SLIDE_DURATION}ms ease-in-out`;
      banner.style.transform = 'translateY(100%)';
      banner.style.opacity = '0';

      // Hide after animation completes
      setTimeout(() => {
        banner.classList.add('hidden');
      }, SLIDE_DURATION);
    }
  }

  /**
   * Show the cookie notice with animation
   */
  function showNotice() {
    const banner = document.getElementById('cookie-notice-banner');
    if (!banner) return;

    // Show banner
    banner.classList.remove('hidden');

    // Apply slide-up animation
    if (!prefersReducedMotion()) {
      // Start from bottom (hidden position)
      banner.style.transform = 'translateY(100%)';
      banner.style.opacity = '0';

      // Trigger animation on next frame
      requestAnimationFrame(() => {
        banner.style.transition = `transform ${SLIDE_DURATION}ms ease-in-out, opacity ${SLIDE_DURATION}ms ease-in-out`;
        banner.style.transform = 'translateY(0)';
        banner.style.opacity = '1';
      });
    }
  }

  /**
   * Initialize cookie notice
   */
  function initCookieNotice() {
    // Check if consent already given
    if (hasValidConsent()) {
      return; // Don't show banner
    }

    // Show banner
    showNotice();

    // Attach dismiss handler
    const dismissButton = document.getElementById('cookie-notice-dismiss');
    if (dismissButton) {
      dismissButton.addEventListener('click', dismissNotice);

      // Keyboard accessibility: Allow Enter and Space to dismiss
      dismissButton.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          dismissNotice();
        }
      });
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCookieNotice);
  } else {
    initCookieNotice();
  }

  // Re-initialize for theme editor (following theme-editor.js pattern)
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', initCookieNotice);
  }
})();
